import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { ITenantLite } from './models/tenant-lite.model';
import { ITenant } from './models/tenant.model';
import { StorageService } from '../storage/storage.service';
import { SanitizationService } from '../sanitization/sanitization.service';
import { PlatformService } from '../platform/platform.service';

/** localStorage key holding the persisted tenant-list length. See initializeTenantListLength(). */
export const TENANT_LIST_LENGTH_KEY = 'tenantListLength';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  public  tenantSignal = signal<ITenant | null>(null);
  public  tenantListSignal = signal<ITenantLite[]>([]);
  public tenantListLengthSignal = signal<number>(0);
  private networksListPageObject: any;
  private logger: Logger;

  private ENDPOINT_FIND_TENANT: string;

  public environmentDomain: string | null = null;
  public tenantDomain: string | null = null;

  private isCordova: boolean = this.platformService.isCordova();

  constructor(
    private environmentService: EnvironmentService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private sanitizationService: SanitizationService,
    private storageService: StorageService,
    private platformService: PlatformService
  ) {
    this.logger = this.loggingService.getLogger("[TenantService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.initializeTenantListLength();
  }

  /**
   * Re-hydrates tenantListLengthSignal from localStorage on service startup.
   * The signal is in-memory only, so on a browser refresh it would reset to 0 and the
   * "Change Home" menu item (shown only when length > 1) would disappear until a fresh
   * tenant-list call completes. Persisting just the length — not the full tenant list —
   * restores the menu immediately without reintroducing a stale list across the
   * grantTenantAccess() first-sign-in flow. See BUG-942828 / BUG-1011694.
   *
   * On Cordova the SQLite DB is initialized asynchronously (StorageService.init →
   * platform.ready → createDB → dbReady), so reading immediately from the ctor would hit
   * an uninitialized db, error, and silently resolve to 0 — defeating the fix on every
   * native cold start. Gate the native read on getDatabaseState() (the same pattern
   * LanguageService / EnvironmentService use); web localStorage is available synchronously.
   */
  private initializeTenantListLength(): void {
    const methodName = "initializeTenantListLength";
    if (this.isCordova) {
      this.storageService.getDatabaseState()
        .pipe(filter((isReady: boolean) => isReady), take(1))
        .subscribe(() => this.hydrateTenantListLength(methodName));
    } else {
      this.hydrateTenantListLength(methodName);
    }
  }

  private hydrateTenantListLength(methodName: string): void {
    this.storageService.getItemFromLocalStorage(TENANT_LIST_LENGTH_KEY, methodName)
      .pipe(take(1))
      .subscribe({
        next: (tenantListLength: number) => {
          this.tenantListLengthSignal.set(tenantListLength || 0);
        },
        error: () => {
          this.tenantListLengthSignal.set(0);
        }
      });
  }

  public setTenantInputValues(userInputTenantDomain: string | null, userInputEnvironmentDomain: string | null): void {
    // VERACODE - SANITIZE //
    this.logger.info('setTenantInputValues() - Setting tenant and environment domains');
    this.tenantDomain = userInputTenantDomain;
    this.environmentDomain = userInputEnvironmentDomain;
  }

  public setIdpLoginToken(idpLoginToken: string | null): Observable<void> {
    return this.storageService.addItemToLocalStorage('idpLoginToken', idpLoginToken, 'setIdpLoginToken()').pipe(
      take(1),
      tap(() => {
        this.logger.info('setIdpLoginToken() - idpLoginToken stored in local');
      })
    );
  }

  private deserializeLinkObjects(tenant: ITenant, links: any): void {
    if (!links) {
      return;
    }

    const linkMappings: Array<{ key: string; property: string }> = [
      { key: 'self', property: 'self' },
      { key: 'px:currentuser', property: 'currentUser' },
      { key: 'px:printers', property: 'printers' },
      { key: 'px:queues', property: 'queues' },
      { key: 'px:commandReleasePrintJob', property: 'releasePrintJob' },
      { key: 'px:commandDeletePrintJob', property: 'deletePrintJob' },
      { key: 'px:networks', property: 'networks' },
      { key: 'px:workstations', property: 'workstations' },
      { key: 'px:workflows', property: 'workflows' },
      { key: 'px:airprintPasswords', property: 'airPrintPasswords' },
      { key: 'px:findPrinterByNfcTag', property: 'findPrinterByNfcTag' },
      { key: 'px:deviceLogUploads', property: 'deviceLogUploads' },
      { key: 'px:idCodes', property: 'idCodes' },
      { key: 'px:userDevices', property: 'userDevices' }
    ];

    linkMappings.forEach(({ key, property }) => {
      const href = links[key]?.href;
      if (href) {
        tenant.links[property] = this.sanitizationService.validateApiUrl(href);
      }
    });
  }

  /**
   * Safely sanitizes non-display text fields from API responses to prevent XSS.
   * Strips HTML tags and dangerous characters. Use for fields NOT shown in templates.
   */
  private sanitizeTextField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeTextInput(value);
    return sanitized || null;
  }

  /**
   * Sanitizes user-provided text fields that will be displayed in templates.
   * Preserves all characters so they render as visible plain text.
   */
  private sanitizeDisplayField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeDisplayText(value);
    return sanitized || null;
  }

  private deserializeEmbeddedObjects(tenant: ITenant, embedded: any): void {
    if (embedded) {
      for (let key in embedded) {
        tenant.embedded[key] = embedded[key];
      }
    }
  }

  private deserializeTenant(input: any): ITenant {
    if (input.hostingDomains) {
      let tenant: ITenant = {
        links: {
          airPrintPasswords: null,
          currentUser: null,
          deletePrintJob: null,
          deviceLogUploads: null,
          findPrinterByNfcTag: null,
          idCodes: null,
          networks: null,
          printers: null,
          queues: null,
          releasePrintJob: null,
          self: null,
          userDevices: null,
          workflows: null,
          workstations: null
        },
        embedded: {
          PUSHER_CLIENT_INFO: input._embedded.PUSHER_CLIENT_INFO ? input._embedded.PUSHER_CLIENT_INFO : null,
          authenticationConfiguration: input._embedded.authenticationConfiguration ? input._embedded.authenticationConfiguration : null,
          productsAndFeatures: input._embedded.productsAndFeatures ? input._embedded.productsAndFeatures : null,
          securePrintConfiguration: input._embedded.securePrintConfiguration ? input._embedded.securePrintConfiguration : null
        },
        tenantData: {
          mobilePrint: input.mobilePrint ? input.mobilePrint : false,
          hostingDomain: this.sanitizeDisplayField(input.hostingDomains[0]),
          name: this.sanitizeDisplayField(input.name)
        }
      };
      if (input._links) {this.deserializeLinkObjects(tenant, input._links);}
      return tenant;
    }
  }

  public refreshStoredTenant(): Observable<void> {
    this.logger.info('refreshStoredTenant()');
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'getLastUsedTenant()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        return this.getTenantResource(lastUsedTenant.links.self).pipe(
          take(1),
          switchMap(() => {
            return of(null);
          })
        )
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('refreshStoredTenant() error');
        return throwError(() => error);
      })
    );
  }

  public getTenantResource(tenantLink: string): Observable<ITenant> {
    this.logger.info('getTenantResource()');
    const embed = '?embed=PUSHER_CLIENT_INFO,securePrintConfiguration';
    return this.httpClient.get<any>(tenantLink + embed).pipe(
      retry(1),
      map((res: any) => {
        const tenant = this.deserializeTenant(res);
        this.tenantSignal.set(tenant);
        return tenant;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('getTenantResource() error');
        return throwError(() => error);
      })
    );
  }

  /** TENANTLIST for multipe tenant select */
  public getTenantListForUser(): Observable<Array<ITenantLite>> {
    this.logger.info('getTenantListForUser()');

    return this.storageService.getItemFromLocalStorage('idpLoginToken', 'getTenantListForUser()').pipe(
      take(1),
      switchMap((idpLoginToken: string | null) => {
        // Security (CWE-601): validate the base endpoint via the trusted
        // sanitizer and rebuild the URL via the URL constructor so the
        // tainted token from local storage cannot influence host/path.
        const rawEndpoint = this.environmentService.getEnvironmentEndpoint['ENDPOINT_GET_TENANT_USER'];
        const validatedEndpoint = this.sanitizationService.validateApiUrl(rawEndpoint);
        if (!validatedEndpoint) {
          this.logger.error('getTenantListForUser() - invalid tenant-user endpoint');
          return throwError(() => new Error('Invalid tenant-user endpoint'));
        }

        const tenantListUrlObj = new URL(validatedEndpoint);
        if (idpLoginToken) {
          const safeToken = this.sanitizationService.validateIdpLoginToken(idpLoginToken);
          if (safeToken) {
            tenantListUrlObj.searchParams.set('idpLoginToken', safeToken);
          } else {
            this.logger.warn('getTenantListForUser() - idpLoginToken failed validation, proceeding without token');
          }
        }
        const tenantListUrl = tenantListUrlObj.toString();

        return this.httpClient.get<any>(tenantListUrl).pipe(
          map((tenantListResponse) => {
            const tenantListFormatted: ITenantLite[] = this.deserializeTenantList(tenantListResponse);
            this.tenantListLengthSignal.set(tenantListFormatted.length);
            this.tenantListSignal.set(tenantListFormatted);

            // Persist only the length so the "Change Home" menu item survives a browser
            // refresh (the signal is in-memory). The full list is intentionally NOT stored —
            // it can become stale across the grantTenantAccess() flow and must be re-fetched
            // from the server. See BUG-942828 / BUG-1011694.
            // Fire-and-forget on purpose: the signals above already reflect the fetch, so a
            // storage write failure must not block the tenant list resolving. Any error is
            // swallowed by StorageService.addItemToLocalStorage's own catchError.
            this.storageService.addItemToLocalStorage(TENANT_LIST_LENGTH_KEY, tenantListFormatted.length, 'getTenantListForUser()').pipe(take(1)).subscribe();

            // Note: idpLoginToken is NOT cleared here — it may still be needed by
            // grantTenantAccess() if the tenant list contains a px:grantTenantAccess link.
            // It will be cleared after grantTenantAccess completes or after selectTenant succeeds.

            return tenantListFormatted;
          }),
          catchError((error: HttpErrorResponse) => {
            this.logger.error('getTenantListForUser() error - status: ' + (error?.status || 'unknown')
              + ', statusText: ' + (error?.statusText || '')
              + ', error: ' + JSON.stringify(error?.error || 'no error body'));
            return throwError(() => error);
          })
        );
      })
    );
  }

  private deserializeTenantList(tenantsRaw: any): Array<ITenantLite> {
    this.logger.info('deserializeTenantList()');
    const tenantList: Array<ITenantLite> = [];
    for (const tenant of tenantsRaw._embedded.tenants) {
      // Strip RFC 6570 URI template syntax (e.g. {?embed}, {?page,size}) from href values
      // before validation — the API returns templated links but the app uses plain URLs
      const selfHref = this.stripUriTemplate(tenant._links['px:tenant']?.href);
      const currentUserHref = this.stripUriTemplate(tenant._links['px:tenant-user']?.href);

      const validatedSelf = this.sanitizationService.validateApiUrl(selfHref);
      const validatedCurrentUser = this.sanitizationService.validateApiUrl(currentUserHref);

      // Skip tenants with missing or invalid required links — this indicates a server-side
      // issue (missing link, untrusted domain, or malformed URL). Log and continue so the
      // remaining valid tenants are still usable.
      if (!validatedSelf || !validatedCurrentUser) {
        const logMsg = `deserializeTenantList() — skipping tenant "${tenant.name}" (hostingDomain: ${tenant.hostingDomain}): `
          + `missing required link(s) — self: ${validatedSelf ?? 'null'}, currentUser: ${validatedCurrentUser ?? 'null'}`;
        this.logger.warn(this.sanitizationService.sanitizeLogOutput(logMsg));
        continue;
      }

      // grantTenantAccess is optional — only present when the user needs to be granted access
      // to this tenant (e.g. first-time IDP sign-in or re-added user).
      // The href contains a URI template: .../grantAccess?idpLoginToken={idpLoginToken}
      // We strip the template here; the actual idpLoginToken is appended at call time.
      const grantAccessHref = tenant._links['px:grantTenantAccess']
        ? this.stripUriTemplate(tenant._links['px:grantTenantAccess']?.href)
        : null;

      const tenantLite: ITenantLite = {
        links: {
          self: validatedSelf,
          currentUser: validatedCurrentUser,
          ...(grantAccessHref ? { grantTenantAccess: this.sanitizationService.validateApiUrl(grantAccessHref) } : {})
        },
        tenantData: {
          name: tenant.name,
          hostingDomain: tenant.hostingDomain,
          intune: tenant.intune ? tenant.intune : null
        }
      };
      tenantList.push(tenantLite);
    }
    this.logger.info('deserializeTenantList() RETURN ');
    return tenantList;
  }

  /**
   * Strips RFC 6570 URI template expressions from a URL string.
   * e.g. "https://api.example.com/users/123{?embed}" → "https://api.example.com/users/123"
   */
  private stripUriTemplate(url: string | undefined | null): string | null {
    if (!url || typeof url !== 'string') {
      return null;
    }
    return url.replace(/\{[^}]*\}/g, '');
  }

  /**
   * Grants the current user access to a tenant. Required for first-time IDP sign-in
   * or when a user has been removed and re-added to a tenant.
   * The server uses the idpLoginToken to verify the user's identity and grant access.
   *
   * The grantAccessUrl comes from stripUriTemplate() which strips {idpLoginToken} from
   * "grantAccess?idpLoginToken={idpLoginToken}", leaving "grantAccess?idpLoginToken=".
   * We use URL + searchParams to safely set the token value (handles encoding automatically).
   */
  public grantTenantAccess(grantAccessUrl: string, idpLoginToken: string): Observable<any> {
    this.logger.info('grantTenantAccess()');
    const parsed = new URL(grantAccessUrl);
    parsed.searchParams.set('idpLoginToken', idpLoginToken);
    return this.httpClient.post<any>(parsed.toString(), null).pipe(
      take(1),
      tap(() => {
        this.logger.info('grantTenantAccess() - Access granted successfully');
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('grantTenantAccess() error - status: ' + (error?.status || 'unknown'));
        return throwError(() => error);
      })
    );
  }

  public getTenantNetworks(netWorksEndpoint: string, storedNetworks: Array<string>, page: number, pageSize: number, sort: string, direction: string, searchString: string): Observable<Array<any>> {
    this.logger.info('getTenantNetworks()');
    let getNetworksUrl = netWorksEndpoint + '?page=' + page + '&pageSize=' + pageSize + '&sort=' + sort + '&direction=' + direction;

    if (searchString) {
      // URL encode the search string to prevent malformed URLs and injection attacks
      getNetworksUrl = getNetworksUrl + '&match=' + encodeURIComponent(searchString);
    }

    if (storedNetworks.length > 0) {
      storedNetworks.forEach(nw => {
        getNetworksUrl += '&networkIds=' + nw;
      });
    }

    return this.httpClient.get<any>(getNetworksUrl).pipe(
        retry(1),
        tap((response: any) => {
          this.networksListPageObject = {
            page: response.page
          };
        }),
        map((response: any) => this.deserializeTenantNetworks(response)),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('getTenantNetworks() error');
          return throwError(() => error);
        })
      );
  }

  private deserializeTenantNetworks(input: any): Array<any> {
    let tenantNetworksList: Array<any> = [];

    if (input._embedded) {
      for (let network of input._embedded['px:networkResources']) {
        tenantNetworksList.push(this.deserializeNetwork(network));
      }
      tenantNetworksList.push(this.networksListPageObject);
    }

    return tenantNetworksList;
  }

  private deserializeNetwork(input: any): any {
    let network: any = {
      links: {
        self: input._links.self.href,
      },
      name: input.name
    };
    return network;
  }

  public resetTenant(): Observable<void> {
    this.logger.info('resetTenant()');
    this.tenantListSignal.set([]);
    this.tenantListLengthSignal.set(0);
    return of(this.tenantSignal.set(null));
  }

  public checkIfUserTenantExists(tenantDomain): Observable<any> {
    this.logger.info('checkIfUserTenantExists()');

    // Security: Validate tenant domain using centralized validation to prevent open redirect (CWE-601)
    const validatedDomain = this.sanitizationService.validateTenantDomain(tenantDomain);

    if (!validatedDomain) {
      this.logger.error('Invalid tenant domain after validation: ' + tenantDomain);
      return throwError(() => new Error('Invalid tenant domain'));
    }

    this.ENDPOINT_FIND_TENANT = this.environmentService.getEnvironmentEndpoint['ENDPOINT_FIND_TENANT'];
    const url = this.ENDPOINT_FIND_TENANT + validatedDomain;
    return this.httpClient.get<any>(url).pipe(
      retry(1),
      map((response: any) => {
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('checkIfUserTenantExists() error');
        return throwError(() => error);
      })
    );
  }
}
